import { observer } from 'mobx-react-lite'
import React, { useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import { TextInputWithLabel } from '../components/Input'
import { ProfilePicture } from '../components/ProfilePicture'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import Radom, { IOrganization } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { isEqual, pickBy } from 'lodash'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsGrid,
  SettingsHero,
  SettingsPage,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const NameInput = styled.input<{ $isEditing: boolean }>`
  background: transparent;
  width: 100%;
  border: none;
  box-shadow: none;
  color: var(--ink, #20242d);
  font-size: 24px;
  font-weight: 500;
  border-radius: 0;
  padding: 0 0 4px;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid ${({ $isEditing }) => $isEditing ? RADOM_COLORS.GRAY_DARK : 'transparent'};

  :focus {
    border-color: ${RADOM_COLORS.ORANGE};
  }
`

const ProfileSummary = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr);
  min-width: 0;
`

const ProfileIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const ProfileActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 2px;
`

const emptyProfile: any = {
  organizationId: '',
  name: '',
  logoUrl: '',
  website: '',
  telegram: '',
  discord: '',
  twitter: '',
  imageBytes: undefined
}

const ProfileSettings = observer((): React.ReactElement => {
  const [profile, updateProfile] = useReducer((prev: any, next: any) => {
    if (next.__replace) {
      const { __replace, ...rest } = next
      return { ...emptyProfile, ...Object.fromEntries(Object.entries(rest).filter(([_, v]) => v != null)) }
    }
    return { ...prev, ...Object.fromEntries(Object.entries(next).filter(([_, v]) => v != null)) }
  }, emptyProfile)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileUpdating, setProfileUpdating] = useState(false)
  const [profileSnapshot, setProfileSnapshot] = useState<IOrganization>(emptyProfile)
  const [profileChanges, setProfileChanges] = useState<Partial<IOrganization>>({})
  const [imgUrl, setImgUrl] = useState<string | undefined>()
  const [imgFile, setImgFile] = useState<File | undefined>()

  useEffect(() => {
    setProfileLoading(true)
    Radom.getSellerProfile()
      .then(p => {
        updateProfile({ __replace: true, ...p })
        setProfileSnapshot(p)
        if (p.logoUrl) setImgUrl(p.logoUrl)
      })
      .catch(err => console.error('Failed to load profile', err))
      .finally(() => setProfileLoading(false))
  }, [])

  useEffect(() => {
    setProfileChanges(pickBy(profile, (v, k) => !isEqual(profileSnapshot[k], v)))
  }, [profile, profileSnapshot])

  useEffect(() => {
    if (imgFile) {
      setImgUrl(URL.createObjectURL(imgFile))
      imgFile.arrayBuffer().then(b => updateProfile({ imageBytes: Array.from(new Uint8Array(b)) }))
    }
  }, [imgFile])

  const onUpdateProfile = async (): Promise<void> => {
    if (profileUpdating) return
    setProfileUpdating(true)
    try {
      await Radom.updateSellerProfile({ organizationId: profile.organizationId, ...profileChanges })
      Radom.getSellerProfile()
      setProfileSnapshot(profile)
      successToast('Successfully updated seller profile')
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    }
    setProfileUpdating(false)
  }

  const onCancelEdit = (): void => {
    updateProfile({ __replace: true, ...profileSnapshot })
    setImgFile(undefined)
    setImgUrl(profileSnapshot.logoUrl)
  }

  const hasProfileChanges = Object.keys(profileChanges).length > 0
  const isProfileComplete = Boolean(profile.name && profile.website && (profile.logoUrl || imgUrl || imgFile))

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Profile</SettingsTitle>
            <SettingsSubtext>
              Manage your seller identity — the name, logo, and links shown on contact-facing payment
              pages and receipts.
            </SettingsSubtext>
          </div>
          <StatusPill $tone={profileLoading ? 'neutral' : isProfileComplete ? 'green' : 'amber'}>
            {profileLoading ? 'Loading' : isProfileComplete ? 'Complete' : 'Needs attention'}
          </StatusPill>
        </SettingsHero>

        {profileLoading
          ? <Spinner />
          : <>
            <SettingsCard>
              <SettingsCardHeader style={{marginBottom:12}}>
                <ProfileSummary>
                  <ProfilePicture imgUrl={imgUrl} imgFile={imgFile} setImgFile={setImgFile} isEditing />
                  <ProfileIdentity>
                    <SettingsCardTitle>Seller identity</SettingsCardTitle>
                    <NameInput
                      value={profile.name}
                      placeholder={profile.sellerAddress || 'Merchant name'}
                      onChange={(e) => updateProfile({ name: (e.target as HTMLInputElement).value })}
                      $isEditing
                    />
                    <SettingsCardDescription>
                      This name appears on contact-facing payment pages and receipts.
                    </SettingsCardDescription>
                  </ProfileIdentity>
                </ProfileSummary>
              </SettingsCardHeader>

              <SettingsCardBody>
                <SettingsGrid>
                  <TextInputWithLabel
                    label="Website URL"
                    value={profile.website}
                    placeholder="https://radom.com/"
                    onChange={(e) => updateProfile({ website: (e.target as HTMLInputElement).value })}
                  />
                  <TextInputWithLabel
                    label="Telegram"
                    value={profile.telegram}
                    placeholder="@radomnetwork"
                    onChange={(e) => updateProfile({ telegram: (e.target as HTMLInputElement).value })}
                  />
                  <TextInputWithLabel
                    label="Discord"
                    value={profile.discord}
                    placeholder="https://discord.gg/invite/aX3Rtv"
                    onChange={(e) => updateProfile({ discord: (e.target as HTMLInputElement).value })}
                  />
                  <TextInputWithLabel
                    label="X (Formerly Twitter)"
                    value={profile.twitter}
                    placeholder="@radomnetwork"
                    onChange={(e) => updateProfile({ twitter: (e.target as HTMLInputElement).value })}
                  />
                </SettingsGrid>
              </SettingsCardBody>
            </SettingsCard>

            {hasProfileChanges && (
              <ProfileActions>
                <SecondaryButton
                  style={{ color: RADOM_COLORS.GRAY_DARKER }}
                  onClick={onCancelEdit}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  isLoading={profileUpdating}
                  style={{ minWidth: 220 }}
                  disabled={profileUpdating}
                  onClick={onUpdateProfile}>
                  Save changes
                </PrimaryButton>
              </ProfileActions>
            )}
          </>}

      </SettingsPage>
    </Container>
  )
})

export default ProfileSettings
